import { TicketService } from '@/lib/services/ticket'
import type { ParentTicketFormValues } from '@/types/ticket'
import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import { storesMatch } from '@/components/forms/parentTicket/feDispatchSelectionUtils'
import {
  collectPrimaryClearScheduleIds,
  resolveDispatchScheduleIds,
  resolveDispatchScheduleIdsForClear,
} from '@/components/forms/parentTicket/feDispatchScheduleIds'
import { isAdditionalFeRole, normalizeFeDispatchRole } from '@/components/forms/parentTicket/feDispatchRole'
import { parentLevelFeSchedules, scheduleChildTicketId } from '@/components/forms/parentTicket/feDispatchUtils'
import type { ParentTicketWithMeta } from '@/store/parentTicket'
import type { FESchedule } from '@/types/parentTicket'

export type DispatchSnapshotContext = {
  certificationRequired?: boolean
  brandId?: number | null
  requiredCertifications?: string[]
  activityFromLocal?: string
  activityToLocal?: string
}

function normalizeCoord(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return ''
  return Number(value).toFixed(6)
}

function normalizeCertList(certs: string[] | undefined): string {
  if (!certs?.length) return ''
  return [...certs].map((c) => c.trim()).filter(Boolean).sort().join(',')
}

/** Stable key for comparing dispatch selections (skip redundant persist on Save). */
export function dispatchSelectionSnapshot(
  primary: FEDispatchSelection | null,
  additionalFes: FEDispatchSelection[] = [],
  context?: DispatchSnapshotContext,
): string {
  if (!primary) return ''
  const additionalPart = [...additionalFes]
    .sort((a, b) => Number(a.feUserId) - Number(b.feUserId))
    .map(
      (b) =>
        `${b.feUserId}:${b.role}:${b.scheduleDate}:${b.scheduleEndDate ?? ''}:${b.scheduleTime}:${b.scheduleEndTime}`,
    )
    .join(',')
  const locationIdentity = [
    primary.locationType ?? 'store',
    primary.locationDisplayName ?? '',
    primary.storeName ?? '',
    primary.storeAddress ?? '',
  ].join(':')
  return [
    primary.feUserId,
    primary.role,
    primary.scheduleDate,
    primary.scheduleEndDate ?? '',
    primary.scheduleTime,
    primary.scheduleEndTime,
    primary.storeId ?? '',
    primary.storeCode ?? '',
    locationIdentity,
    normalizeCoord(primary.latitude),
    normalizeCoord(primary.longitude),
    additionalPart,
    context?.certificationRequired ? '1' : '0',
    context?.brandId ?? '',
    normalizeCertList(context?.requiredCertifications),
    context?.activityFromLocal ?? '',
    context?.activityToLocal ?? '',
  ].join('|')
}

export type DispatchExistingScheduleIds = {
  primary: number | null
  additionalFes: number[]
}

export type PersistDispatchResult =
  | { success: true; deletedScheduleIds?: number[] }
  | { success: false; message: string }

export const ADDITIONAL_MUST_DIFFER_FROM_PRIMARY_MESSAGE =
  'Additional field engineer must be different from the primary field engineer.'

export const PRIMARY_MUST_DIFFER_FROM_ADDITIONAL_MESSAGE =
  'Primary field engineer must be different from the additional field engineer.'

/** @deprecated Use ADDITIONAL_MUST_DIFFER_FROM_PRIMARY_MESSAGE */
export const BACKUP_MUST_DIFFER_FROM_PRIMARY_MESSAGE = ADDITIONAL_MUST_DIFFER_FROM_PRIMARY_MESSAGE
/** @deprecated Use PRIMARY_MUST_DIFFER_FROM_ADDITIONAL_MESSAGE */
export const PRIMARY_MUST_DIFFER_FROM_BACKUP_MESSAGE = PRIMARY_MUST_DIFFER_FROM_ADDITIONAL_MESSAGE

export function getEngineerAssignmentConflict(
  role: 'primary' | 'additional' | 'secondary',
  feUserId: number,
  primaryFeUserId: number | null | undefined,
  additionalFeUserIds: number[],
): string | null {
  const engineerId = Number(feUserId)
  const primaryId = primaryFeUserId != null ? Number(primaryFeUserId) : null
  const additionals = additionalFeUserIds.map(Number).filter((id) => id > 0)
  const normalizedRole = normalizeFeDispatchRole(role)

  if (normalizedRole === 'additional') {
    if (primaryId != null && engineerId === primaryId) {
      return ADDITIONAL_MUST_DIFFER_FROM_PRIMARY_MESSAGE
    }
    if (additionals.includes(engineerId)) {
      return 'Each additional field engineer must be unique.'
    }
  }

  if (normalizedRole === 'primary' && additionals.includes(engineerId)) {
    return PRIMARY_MUST_DIFFER_FROM_ADDITIONAL_MESSAGE
  }

  return null
}

function isMissingScheduleResponse(result: { status?: number; message?: string }): boolean {
  const message = result.message ?? ''
  if (result.status === 404) return true
  // Laravel ModelNotFoundException (often wrapped as 422 MessageService status:error)
  if (/No query results for model/i.test(message) && /FESchedule/i.test(message)) return true
  if (
    (result.status === 422 || result.status === 500) &&
    /No query results for model/i.test(message)
  ) {
    return true
  }
  return false
}

async function patchEngineerAssignments(
  ticketId: string,
  fields: {
    primaryEngineerId?: number | null
    additionalFeUserIds?: number[]
  },
): Promise<PersistDispatchResult> {
  const res = await TicketService.patchParentTicket(ticketId, fields)
  if (!res.success) {
    return {
      success: false,
      message: res.message || 'Failed to update field engineer assignments',
    }
  }
  return { success: true }
}

function childTicketIdForSchedule(
  existingScheduleId: number | null,
  selection: FEDispatchSelection,
  schedules: FESchedule[],
): number | null {
  const fromSelection = selection.childTicketId ?? selection.child_ticket_id
  if (fromSelection != null && Number(fromSelection) > 0) return Number(fromSelection)
  if (existingScheduleId == null) return null
  const match = schedules.find((s) => s.id === existingScheduleId)
  return scheduleChildTicketId(match ?? {}) ?? null
}

async function saveDispatchSchedule(
  ticketId: string,
  selection: FEDispatchSelection,
  existingScheduleId: number | null,
  schedules: FESchedule[] = [],
) {
  const schedulePayload = {
    fe_user_id: selection.feUserId,
    schedule_date: selection.scheduleDate,
    schedule_time: selection.scheduleTime,
    schedule_end_time: selection.scheduleEndTime,
    schedule_end_date: selection.spansMultipleDays ? (selection.scheduleEndDate ?? null) : null,
    latitude: selection.latitude,
    longitude: selection.longitude,
    location_address: selection.storeAddress,
    contact_number_override: selection.contactNumberOverride ?? null,
    role: normalizeFeDispatchRole(selection.role),
  }

  if (existingScheduleId == null) {
    return await TicketService.createFESchedule(ticketId, {
      ...schedulePayload,
      create_fe_visit_task: true,
    })
  }

  const existingChildId = childTicketIdForSchedule(existingScheduleId, selection, schedules)
  // Missing/unknown child → send true so orphans get linked (backend is idempotent when already linked).
  const updatePayload = {
    ...schedulePayload,
    ...(existingChildId == null ? { create_fe_visit_task: true } : {}),
  }

  const updateRes = await TicketService.updateFESchedule(ticketId, existingScheduleId, updatePayload)
  // After Clear (force-delete), cached IDs may point at removed rows — recreate.
  if (!updateRes.success && isMissingScheduleResponse(updateRes)) {
    return await TicketService.createFESchedule(ticketId, {
      ...schedulePayload,
      create_fe_visit_task: true,
    })
  }

  return updateRes
}

function assertDistinctEngineers(
  primary: FEDispatchSelection,
  additionalFes: FEDispatchSelection[],
): PersistDispatchResult | null {
  const ids = [primary.feUserId]
  for (const additional of additionalFes) {
    if (additional.feUserId === primary.feUserId) {
      return { success: false, message: ADDITIONAL_MUST_DIFFER_FROM_PRIMARY_MESSAGE }
    }
    if (ids.includes(additional.feUserId)) {
      return { success: false, message: 'Each additional field engineer must be unique.' }
    }
    ids.push(additional.feUserId)
  }
  return null
}

/** Link store + create/update primary and optional additional FE schedules for a ticket. */
export async function persistDispatchSelection(
  ticketId: string,
  form: ParentTicketFormValues,
  primary: FEDispatchSelection | null,
  additionalFes: FEDispatchSelection[] = [],
  existingIds: DispatchExistingScheduleIds = { primary: null, additionalFes: [] },
  ticketMeta?: ParentTicketWithMeta,
): Promise<PersistDispatchResult> {
  if (!primary) {
    return { success: false, message: 'Primary field engineer dispatch is required' }
  }

  const distinctError = assertDistinctEngineers(primary, additionalFes)
  if (distinctError) return distinctError

  const liveIds = await resolveDispatchScheduleIdsForClear(
    ticketId,
    ticketMeta,
    primary,
    additionalFes,
    existingIds,
  )
  const mergedExistingIds: DispatchExistingScheduleIds = {
    primary: liveIds.primary,
    additionalFes: liveIds.additionalFes,
  }

  const liveSchedulesRes = await TicketService.getFESchedules(ticketId)
  const liveSchedules =
    liveSchedulesRes.success && liveSchedulesRes.data
      ? liveSchedulesRes.data
      : parentLevelFeSchedules(ticketMeta?.feSchedules ?? [])

  const additionalIds = additionalFes.map((b) => b.feUserId)
  const engineerRes = await TicketService.updateParentTicket(ticketId, {
    ...form,
    primaryEngineerId: primary.feUserId,
    additionalFeUserIds: additionalIds,
  } as ParentTicketFormValues)
  if (!engineerRes.success) {
    return { success: false, message: engineerRes.message || 'Failed to save field engineer assignments' }
  }

  const keptExistingIds = new Set<number>()
  for (let i = 0; i < additionalFes.length; i++) {
    const existingId = mergedExistingIds.additionalFes[i] ?? null
    if (existingId != null) keptExistingIds.add(existingId)
  }

  for (const existingAdditionalId of mergedExistingIds.additionalFes) {
    if (keptExistingIds.has(existingAdditionalId)) continue
    const deleteRes = await TicketService.deleteFESchedule(ticketId, existingAdditionalId)
    if (!deleteRes.success && !isMissingScheduleResponse(deleteRes)) {
      return { success: false, message: deleteRes.message || 'Failed to remove additional FE schedule' }
    }
  }

  const isStoreSelection = primary.locationType !== 'location'
  const storeUnchanged = isStoreSelection && storesMatch(form.storeId, form.storeCode, primary.storeId, primary.storeCode)

  if (!isStoreSelection) {
    const locationRes = await TicketService.updateParentTicket(ticketId, {
      ...form,
      storeId: '',
      storeCode: '',
    })
    if (!locationRes.success) {
      return { success: false, message: locationRes.message || 'Failed to save incident location' }
    }
  } else if (!storeUnchanged) {
    const storeRes = await TicketService.updateParentTicket(ticketId, {
      ...form,
      storeId: primary.storeId ?? undefined,
      storeCode: primary.storeCode,
    })
    if (!storeRes.success) {
      return { success: false, message: storeRes.message || 'Failed to link store' }
    }
  }

  const primaryRes = await saveDispatchSchedule(ticketId, primary, mergedExistingIds.primary, liveSchedules)
  if (!primaryRes.success) {
    return {
      success: false,
      message:
        primaryRes.message ||
        (mergedExistingIds.primary != null
          ? 'Failed to update primary FE schedule'
          : 'Failed to create primary FE schedule'),
    }
  }

  for (let i = 0; i < additionalFes.length; i++) {
    const additional = additionalFes[i]
    const existingId = mergedExistingIds.additionalFes[i] ?? null
    const additionalRes = await saveDispatchSchedule(ticketId, additional, existingId, liveSchedules)
    if (!additionalRes.success) {
      return {
        success: false,
        message:
          additionalRes.message ||
          (existingId != null
            ? `Failed to update additional FE schedule #${i + 1}`
            : `Failed to create additional FE schedule #${i + 1}`),
      }
    }
  }

  return { success: true }
}

export type ClearPersistedDispatchOptions = {
  scope: 'primary' | 'additional' | 'all-additional'
  /** 0-based additional slot index when scope is 'additional'. */
  additionalSlotIndex?: number
  form: ParentTicketFormValues
  scheduleIds: DispatchExistingScheduleIds
  /** Ticket meta for schedule ID resolution when cached IDs are missing. */
  ticketMeta?: ParentTicketWithMeta
  dispatchPrimary?: FEDispatchSelection | null
  dispatchAdditionalFes?: FEDispatchSelection[]
  /** FE user IDs to unlock after removal (best-effort). */
  engineerUserIdsToUnlock: number[]
  /** Remaining additional FE user IDs after a single-additional clear. */
  remainingAdditionalFeUserIds?: number[]
  /**
   * When true (dispatch checkbox uncheck), also clear store/location/schedule
   * context on the parent ticket so the recommender starts clean.
   */
  clearDispatchContext?: boolean
  /** Custom reason for withdrawal (from modal input). */
  reason?: string
}

async function withdrawOrDeleteSchedule(
  ticketId: string,
  schedule: FESchedule,
  reason?: string,
): Promise<PersistDispatchResult | null> {
  const childId = scheduleChildTicketId(schedule)
  if (childId != null && schedule.id != null) {
    const withdrawRes = await TicketService.withdrawFESchedule(ticketId, schedule.id, {
      reason: reason ?? 'Dispatch withdrawn from parent-ticket edit',
    })
    if (!withdrawRes.success) {
      return {
        success: false,
        message: withdrawRes.message || 'Failed to withdraw linked FE dispatch',
      }
    }
    return null
  }

  if (schedule.id == null) return null
  return deleteScheduleId(ticketId, schedule.id)
}

async function deleteScheduleId(ticketId: string, scheduleId: number): Promise<PersistDispatchResult | null> {
  const deleteRes = await TicketService.deleteFESchedule(ticketId, scheduleId)
  if (!deleteRes.success && !isMissingScheduleResponse(deleteRes)) {
    return { success: false, message: deleteRes.message || 'Failed to remove FE schedule' }
  }
  return null
}

async function removeScheduleById(
  ticketId: string,
  scheduleId: number,
  liveSchedules: FESchedule[] = [],
  reason?: string,
): Promise<PersistDispatchResult | null> {
  const schedule = liveSchedules.find((s) => Number(s.id) === Number(scheduleId))
  if (schedule && scheduleChildTicketId(schedule) != null) {
    return withdrawOrDeleteSchedule(ticketId, schedule, reason)
  }
  return deleteScheduleId(ticketId, scheduleId)
}

async function deleteScheduleIds(
  ticketId: string,
  scheduleIds: number[],
  liveSchedules: FESchedule[] = [],
  reason?: string,
): Promise<PersistDispatchResult | null> {
  const unique = [...new Set(scheduleIds.filter((id) => id > 0))]
  for (const id of unique) {
    const err = await removeScheduleById(ticketId, id, liveSchedules, reason)
    if (err) return err
  }
  return null
}

function additionalFeUserIdForSlot(
  slotIndex: number,
  scheduleIds: DispatchExistingScheduleIds,
  dispatchAdditionalFes: FEDispatchSelection[],
  ticketMeta?: ParentTicketWithMeta,
  dispatchPrimary?: FEDispatchSelection | null,
): number | null {
  const fromSelection = dispatchAdditionalFes[slotIndex]?.feUserId
  if (fromSelection != null && fromSelection > 0) return Number(fromSelection)

  const resolved = resolveDispatchScheduleIds(ticketMeta, dispatchPrimary ?? null, dispatchAdditionalFes)
  const scheduleId = resolved.additionalFes[slotIndex]
  if (scheduleId == null) return null

  const schedules = parentLevelFeSchedules(ticketMeta?.feSchedules ?? [])
  const match = schedules.find((s) => s.id === scheduleId)
  return match?.fe_user_id ?? null
}

async function resolveClearScheduleIds(
  ticketId: string,
  options: ClearPersistedDispatchOptions,
): Promise<{
  scheduleIds: DispatchExistingScheduleIds
  liveSchedules: FESchedule[]
  liveFetchFailed: boolean
  liveFetchMessage?: string
}> {
  const { ticketMeta, dispatchPrimary, dispatchAdditionalFes = [], scheduleIds: cached } = options

  const liveRes = await TicketService.getFESchedules(ticketId)
  const liveSchedules = liveRes.success && liveRes.data ? liveRes.data : []

  const merged = resolveDispatchScheduleIds(
    ticketMeta,
    dispatchPrimary ?? null,
    dispatchAdditionalFes,
    liveSchedules.length > 0 ? liveSchedules : undefined,
  )

  const scheduleIds: DispatchExistingScheduleIds = {
    primary: merged.primary ?? cached.primary,
    additionalFes: merged.additionalFes.length > 0 ? merged.additionalFes : cached.additionalFes,
  }

  if (liveSchedules.length === 0) {
    const cachedResolved = await resolveDispatchScheduleIdsForClear(
      ticketId,
      ticketMeta,
      dispatchPrimary ?? null,
      dispatchAdditionalFes,
    )
    scheduleIds.primary = scheduleIds.primary ?? cachedResolved.primary
    if (scheduleIds.additionalFes.length === 0) {
      scheduleIds.additionalFes = cachedResolved.additionalFes
    }
  }

  return {
    scheduleIds,
    liveSchedules,
    liveFetchFailed: !liveRes.success,
    liveFetchMessage: liveRes.message,
  }
}

async function verifyParentSchedulesClearedForEngineers(
  ticketId: string,
  engineerUserIds: number[],
): Promise<PersistDispatchResult | null> {
  const res = await TicketService.getFESchedules(ticketId)
  if (!res.success) {
    return { success: false, message: res.message ?? 'Failed to verify FE schedule removal' }
  }

  const engineerSet = new Set(engineerUserIds.map(Number).filter((id) => id > 0))
  const remaining = parentLevelFeSchedules(res.data ?? []).filter((s) =>
    engineerSet.has(Number(s.fe_user_id)),
  )

  if (remaining.length > 0) {
    return { success: false, message: 'Failed to remove all FE schedules' }
  }

  return null
}

/** Remove persisted primary/additional FE assignments from a ticket (edit mode Clear). */
export async function clearPersistedDispatch(
  ticketId: string,
  options: ClearPersistedDispatchOptions,
): Promise<PersistDispatchResult> {
  const {
    scope,
    form,
    engineerUserIdsToUnlock,
    dispatchAdditionalFes = [],
    ticketMeta,
    clearDispatchContext = false,
    reason,
  } = options
  const { scheduleIds, liveSchedules, liveFetchFailed, liveFetchMessage } = await resolveClearScheduleIds(
    ticketId,
    options,
  )

  if (scope === 'primary') {
    const metaSchedules = parentLevelFeSchedules(ticketMeta?.feSchedules ?? [])
    const idsToDelete = collectPrimaryClearScheduleIds(
      liveSchedules,
      scheduleIds,
      metaSchedules,
      engineerUserIdsToUnlock,
    )

    const expectedSchedules =
      metaSchedules.length > 0 ||
      liveSchedules.length > 0 ||
      scheduleIds.primary != null ||
      scheduleIds.additionalFes.length > 0

    if (idsToDelete.length === 0 && expectedSchedules) {
      return {
        success: false,
        message: liveFetchFailed
          ? liveFetchMessage ?? 'Failed to load FE schedules for clear'
          : 'Could not resolve FE schedule IDs to remove',
      }
    }

    // Try batch withdraw first when there are multiple schedules to withdraw
    if (idsToDelete.length > 1) {
      const batchRes = await TicketService.withdrawFeSchedulesBatch(ticketId, {
        schedule_ids: idsToDelete,
        reason: reason || 'Dispatch withdrawn from parent-ticket edit',
      })
      if (batchRes.success) {
        // The backend batch transaction is authoritative: it withdraws tasks,
        // removes schedules, reconciles parent assignment fields, and releases
        // locks after commit. The caller performs the single coordinated refresh.
        return { success: true, deletedScheduleIds: idsToDelete }
      } else if (batchRes.status === 404 || batchRes.status === 405) {
        // Batch endpoint not available — fall back to sequential
        const deleteErr = await deleteScheduleIds(ticketId, idsToDelete, liveSchedules, reason)
        if (deleteErr) return deleteErr
      } else {
        return { success: false, message: batchRes.message || 'Failed to withdraw FE schedules' }
      }
    } else {
      const deleteErr = await deleteScheduleIds(ticketId, idsToDelete, liveSchedules, reason)
      if (deleteErr) return deleteErr
    }

    const verifyErr = await verifyParentSchedulesClearedForEngineers(ticketId, engineerUserIdsToUnlock)
    if (verifyErr) return verifyErr

    if (clearDispatchContext) {
      const engineerRes = await TicketService.updateParentTicket(
        ticketId,
        {
          ...form,
          primaryEngineerId: null,
          additionalFeUserIds: [],
        } as ParentTicketFormValues,
        { clearDispatchContext: true },
      )
      if (!engineerRes.success) {
        return { success: false, message: engineerRes.message || 'Failed to clear field engineer assignments' }
      }
    } else {
      const engineerRes = await patchEngineerAssignments(ticketId, {
        primaryEngineerId: null,
        additionalFeUserIds: [],
      })
      if (!engineerRes.success) return engineerRes
    }

    return { success: true, deletedScheduleIds: idsToDelete }
  }

  if (scope === 'all-additional') {
    const additionalScheduleIds = [
      ...new Set(
        [
          ...scheduleIds.additionalFes,
          ...parentLevelFeSchedules(liveSchedules)
            .filter((s) => isAdditionalFeRole(s.role))
            .map((s) => s.id)
            .filter((id): id is number => id != null),
          ...parentLevelFeSchedules(ticketMeta?.feSchedules ?? [])
            .filter((s) => isAdditionalFeRole(s.role))
            .map((s) => s.id)
            .filter((id): id is number => id != null),
        ].map(Number).filter((id) => id > 0),
      ),
    ]

    const deleteErr = await deleteScheduleIds(ticketId, additionalScheduleIds, liveSchedules, reason)
    if (deleteErr) return deleteErr

    const engineerRes = await patchEngineerAssignments(ticketId, {
      additionalFeUserIds: [],
    })
    if (!engineerRes.success) return engineerRes

    return { success: true, deletedScheduleIds: additionalScheduleIds }
  }

  const slotIndex = options.additionalSlotIndex ?? 0
  const additionalFeUserId = additionalFeUserIdForSlot(
    slotIndex,
    scheduleIds,
    dispatchAdditionalFes,
    options.ticketMeta,
    options.dispatchPrimary,
  )

  let additionalScheduleId: number | null = scheduleIds.additionalFes[slotIndex] ?? null
  if (additionalScheduleId == null && additionalFeUserId != null) {
    const match = parentLevelFeSchedules(liveSchedules).find(
      (s) => s.fe_user_id === additionalFeUserId && isAdditionalFeRole(s.role),
    )
    additionalScheduleId = match?.id ?? null
  }

  if (additionalScheduleId != null) {
    const err = await removeScheduleById(ticketId, additionalScheduleId, liveSchedules, reason)
    if (err) return err
  }

  const remainingAdditionalIds = options.remainingAdditionalFeUserIds ?? []
  const engineerRes = await patchEngineerAssignments(ticketId, {
    additionalFeUserIds: remainingAdditionalIds,
  })
  if (!engineerRes.success) return engineerRes

  return {
    success: true,
    deletedScheduleIds: additionalScheduleId != null ? [additionalScheduleId] : [],
  }
}
